import { keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, Search, UserRoundCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useModuleFilter } from '@/context/ModuleFilter';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { ConversationListItem } from '@/schemas/commons';
import type { ConversationsParams } from '@/services/Conversations';

import { ConversationsLoading } from './Conversations.loading';
import { useConversations } from './Conversations.requests';
import { styles } from './Conversations.styles';

const PAGE_SIZE = 12;

export const Conversations = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });
	const { selectedModule } = useModuleFilter();
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebouncedValue(search.trim());

	const params: ConversationsParams = {
		page,
		pageSize: PAGE_SIZE,
		moduleName: selectedModule,
		...(debouncedSearch ? { search: debouncedSearch } : {}),
	};
	const { data, isLoading, isError } = useConversations(params, { placeholderData: keepPreviousData });

	const onSearchChange = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	if (isLoading) return <ConversationsLoading />;
	if (isError || !data) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const { items, pagination, totals } = data;
	const from = (pagination.page - 1) * pagination.pageSize + 1;
	const to = Math.min(pagination.page * pagination.pageSize, pagination.totalItems);

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<span className="font-medium text-default">{t('BREADCRUMB')}</span>
				</nav>
				<div className="flex items-center gap-xs">
					<button
						type="button"
						className="inline-flex items-center gap-3xs text-[11px] text-subtle transition-colors hover:text-default"
					>
						<Download className="h-sm w-sm" />
						{t('EXPORT_CSV')}
					</button>
				</div>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-end justify-between gap-md">
					<div>
						<p className={styles.pageEyebrow}>{t('EYEBROW')}</p>
						<h1 className={styles.pageH1}>{t('TITLE')}</h1>
						<p className={styles.pageSub}>{t('SUBTITLE', { count: pagination.totalItems })}</p>
					</div>
					<p className="text-[12px] text-subtle">
						{t('PERIOD_COST')}{' '}
						<span className="font-semibold tabular-nums text-default">${totals.costUsdPeriod.toFixed(2)}</span>
						<span className="mx-2xs text-subtle">·</span>
						{t('PAGE_COST')}{' '}
						<span className="font-semibold tabular-nums text-default">${totals.costUsdFiltered.toFixed(2)}</span>
					</p>
				</header>

				<section className={styles.filterRow}>
					<div className="relative max-w-[288px] flex-1">
						<Search className="pointer-events-none absolute left-xs top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-subtle" />
						<input
							type="search"
							value={search}
							onChange={(e) => onSearchChange(e.target.value)}
							placeholder={t('SEARCH_PLACEHOLDER')}
							className={styles.filterInput}
						/>
					</div>
					<select className={styles.select}>
						<option>Intercom</option>
						<option>{t('FILTER_ALL_CHANNELS')}</option>
					</select>
					<select className={styles.select}>
						<option>{t('FILTER_PERIOD_TODAY')}</option>
						<option>{t('FILTER_PERIOD_7D')}</option>
						<option>{t('FILTER_PERIOD_30D')}</option>
					</select>
					<button
						type="button"
						onClick={() => onSearchChange('')}
						className="ml-auto text-[11px] text-subtle transition-colors hover:text-default"
					>
						{t('CLEAR_FILTERS')}
					</button>
				</section>

				<section>
					<header className="mb-xs flex items-baseline justify-between">
						<div className="text-[12px] text-subtle">
							{t('SHOWING')}{' '}
							<span className="font-medium tabular-nums text-default">
								{from}–{to}
							</span>{' '}
							{t('OF')} <span className="font-medium tabular-nums text-default">{pagination.totalItems}</span>
						</div>
					</header>

					<table className="w-full text-left text-[13px]">
						<thead className="border-b border-subtle">
							<tr>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_THREAD')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_USER')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_COMPANY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_COUNTRY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs text-right`}>{t('COL_MESSAGES')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs text-right`}>{t('COL_COST')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_ACTIVITY')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_STATUS')}</th>
								<th className={`${styles.tableHeader} px-sm py-xs`}>{t('COL_EVALUATION')}</th>
							</tr>
						</thead>
						<tbody>
							{items.map((conv) => (
								<ConversationRow key={conv.threadId} conv={conv} />
							))}
						</tbody>
					</table>

					<footer className="mt-sm flex items-center justify-between border-t border-subtle pt-sm">
						<div className="text-[11px] text-subtle">
							{t('PAGE_SUBTOTAL')}{' '}
							<span className="font-medium tabular-nums text-default">${totals.costUsdFiltered.toFixed(2)}</span>
							<span className="mx-2xs text-subtle">·</span>
							{t('PAGE_OF', { page: pagination.page, totalPages: pagination.totalPages })}
						</div>
						<nav className="flex items-center gap-3xs">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page <= 1}
								className={styles.pageBtn}
								aria-label={t('PREV')}
							>
								<ChevronLeft className="h-[14px] w-[14px]" />
							</button>
							{Array.from({ length: Math.min(3, pagination.totalPages) }).map((_, i) => {
								const p = i + 1;

								return (
									<button
										key={p}
										type="button"
										onClick={() => setPage(p)}
										className={page === p ? styles.pageBtnActive : styles.pageBtn}
									>
										{p}
									</button>
								);
							})}
							{pagination.totalPages > 4 && <span className="px-2xs text-[11px] text-subtle">…</span>}
							{pagination.totalPages > 3 && (
								<button
									type="button"
									onClick={() => setPage(pagination.totalPages)}
									className={page === pagination.totalPages ? styles.pageBtnActive : styles.pageBtn}
								>
									{pagination.totalPages}
								</button>
							)}
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
								disabled={page >= pagination.totalPages}
								className={styles.pageBtn}
								aria-label={t('NEXT')}
							>
								<ChevronRight className="h-[14px] w-[14px]" />
							</button>
						</nav>
					</footer>
				</section>
			</main>
		</>
	);
};

type ConversationRowProps = {
	conv: ConversationListItem;
};

const ConversationRow = ({ conv }: ConversationRowProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });

	return (
		<tr className="relative border-b border-subtle/50 transition-colors hover:bg-grey-25">
			<td className="px-sm py-xs">
				<Link to={`/conversations/${conv.threadId}`} className="after:absolute after:inset-0 after:content-['']">
					<code className="font-mono text-[11px] text-default">{conv.threadId}</code>
				</Link>
			</td>
			<td className="px-sm py-xs">
				<div className="flex items-center gap-2xs">
					<span className={styles.userAvatar}>{conv.userInitials}</span>
					{conv.userName}
				</div>
			</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs`}>{conv.companyName}</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs`}>{conv.country}</td>
			<td className={`${styles.tableCell} px-sm py-xs text-right tabular-nums`}>{conv.messageCount}</td>
			<td
				className={`px-sm py-xs text-right tabular-nums ${
					conv.status === 'tool_error' ? 'font-semibold text-error' : styles.tableCell
				}`}
			>
				${conv.costUsd.toFixed(3)}
			</td>
			<td className={`${styles.tableCellMuted} px-sm py-xs tabular-nums`}>{conv.lastActivity}</td>
			<td className="px-sm py-xs">
				{conv.status === 'active' && (
					<span className={styles.statusActive}>
						<span className={styles.dotSuccess} />
						{t('STATUS_ACTIVE')}
					</span>
				)}
				{conv.status === 'closed' && <span className={styles.statusClosed}>{t('STATUS_CLOSED')}</span>}
				{conv.status === 'handoff' && (
					<span className={styles.statusHandoff}>
						<UserRoundCog className="h-sm w-sm" />
						{t('STATUS_HANDOFF')}
					</span>
				)}
				{conv.status === 'tool_error' && (
					<span className={styles.statusError}>
						<AlertTriangle className="h-sm w-sm" />
						{t('STATUS_TOOL_ERROR')}
					</span>
				)}
			</td>
			<td className="px-sm py-xs">
				<EvaluationSummaryBadge summary={conv.evaluationSummary} />
			</td>
		</tr>
	);
};

const EvaluationSummaryBadge = ({ summary }: { summary: ConversationListItem['evaluationSummary'] }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversations' });

	if (!summary || summary.status === 'not_applicable')
		return <span className="text-[11px] text-subtle">{t('EVAL_NOT_APPLICABLE')}</span>;
	if (summary.status === 'pending')
		return (
			<span className="inline-flex items-center gap-3xs rounded-full bg-grey-25 px-2xs py-3xs text-[10px] text-subtle">
				{t('EVAL_PENDING')}
			</span>
		);
	const failed = summary.totalJudges - summary.passedJudges;

	return (
		<span
			className={`inline-flex items-center gap-3xs rounded-full px-2xs py-3xs text-[10px] font-medium tabular-nums ${
				failed > 0 ? 'bg-red-50 text-error' : 'bg-green-50 text-success'
			}`}
		>
			{t('EVAL_RATIO', { passed: summary.passedJudges, total: summary.totalJudges })}
		</span>
	);
};
